import React, { useCallback, useMemo } from "react";
import { AccountAssociateInfoRow } from "../../../../../../src/api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../../../src/app/store";
import { ColumnType } from "../../../../../components/ui/baseDataTable";
import {
  addAssociateConcernRow,
  deleteAssociateConcernRow,
  selectFMR1DraftAssociateConcernRows,
  updateAssociateConcernRow,
} from "../../../logic/frm1Slice";
import {
  generateRowId,
  getInsertedSno,
  getInsertedSortOrder,
  getSnoUpdates,
  sortRowsBySortOrder,
} from "../common/utils";
import { FMRTable, FMRTableColumn } from "../common/FMRTable";

export type AssociateConcernDetailsTableHandle = {
  getDirtyRows: () => AccountAssociateInfoRow[];
  getDirtyUpsertPayload: () => any[];
  getAllRows: () => AccountAssociateInfoRow[];
  markAllClean: () => void;
  resetRows: (nextRows: AccountAssociateInfoRow[]) => void;
};

export interface AssociateConcernDetailsTableProps {
  onDirtyChange?: (isDirty: boolean) => void;
  onDirtyUpsertPayloadChange?: (payload: any[]) => void;
  initialRows?: AccountAssociateInfoRow[];
}

const buildEmptyRow = (): AccountAssociateInfoRow => ({
  row_id: generateRowId(),
  isDirty: true,
  isAdded: true,
});

export const AssociateConcernDetailsTable: React.FC<AssociateConcernDetailsTableProps> = () => {
  const dispatch = useAppDispatch();

  const rows = useAppSelector(selectFMR1DraftAssociateConcernRows);
  const sortedRows = useMemo(() => sortRowsBySortOrder(rows), [rows]);

  const handleAddRow = useCallback((rowIndex: number) => {
    const prevRow = sortedRows[rowIndex];
    const prevSort = prevRow?.sort_order;
    const nextSort = sortedRows[rowIndex + 1]?.sort_order;
    const newRow: AccountAssociateInfoRow = {
      ...buildEmptyRow(),
      sort_order: getInsertedSortOrder(prevSort, nextSort),
      sno: getInsertedSno(prevRow),
    };

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, +1);
    updates.forEach((u) => {
      dispatch(updateAssociateConcernRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(addAssociateConcernRow({ row: newRow }));
  }, [dispatch, sortedRows]);

  const handleDeleteRow = useCallback(async (rowIndex: number) => {
    const deletedRow = sortedRows[rowIndex];
    if (!deletedRow) return;

    const updates = getSnoUpdates(sortedRows, rowIndex + 1, -1);
    updates.forEach((u) => {
      dispatch(updateAssociateConcernRow({ id: u.id, changes: { sno: u.newSno } }));
    });

    dispatch(deleteAssociateConcernRow({ id: deletedRow.row_id }));

    if (deletedRow.isAdded) return;
  }, [dispatch, sortedRows]);

  const handleChange = useCallback(
    (rowIndex: number, value: any, field: string) => {
      const row = sortedRows[rowIndex];
      if (!row) return;
      dispatch(updateAssociateConcernRow({ id: row.row_id, changes: { [field]: value } as any }));
    },
    [dispatch, sortedRows],
  );

  const columns: FMRTableColumn<AccountAssociateInfoRow>[] = [
    {
      headerName: "Name of Account",
      field: "name_of_account",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.name_of_account ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_account"),
    },

    {
      headerName: "Sr. No. of Account",
      field: "sr_no_of_account",
      type: "number" as ColumnType,
      isEditable: true,
      getValue: (row) => row.sr_no_of_account ?? null,
      onChange: (rowIndex: number, value: number | null) => handleChange(rowIndex, value, "sr_no_of_account"),
    },

    {
      headerName: "Sr. No.",
      field: "sno",
      type: "text" as ColumnType,
      isEditable: false,
      className: "w-[60px]",
      getValue: (row) => row.sno ?? null,
    },

    {
      headerName: "Name of Associate Concern",
      field: "name_of_associate_concern",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.name_of_associate_concern ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "name_of_associate_concern"),
    },

    {
      headerName: "PAN of Associate Concern",
      field: "pan_of_associate_concern",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.pan_of_associate_concern ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "pan_of_associate_concern"),
    },

    {
      headerName: "CIN / LLPIN",
      field: "din",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.din ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "din"),
    },

    {
      headerName: "Associate Concern Address",
      field: "associate_concern_address",
      type: "text" as ColumnType,
      isEditable: true,
      getValue: (row) => row.associate_concern_address ?? "",
      onChange: (rowIndex: number, value: string) => handleChange(rowIndex, value, "associate_concern_address"),
    },
  ];

  return (
    <FMRTable
      columns={columns}
      rows={sortedRows}
      onAdd={handleAddRow}
      onDelete={handleDeleteRow}
      title={"Details of Associate Concern"}
    />
  );
};

AssociateConcernDetailsTable.displayName = "AssociateConcernDetailsTable";
